import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { inviteJoinUrl } from '../../lib/invite-errors';
import { formatDateAU } from '../../lib/formatDate';

interface InviteRow {
  id: string;
  code: string;
  expiresAt: string | null;
  maxUses: number | null;
  useCount: number;
}

interface Props {
  orgId: string;
  orgName: string;
  open: boolean;
  onClose: () => void;
}

const joinLinkOrigin =
  (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined)?.replace(/\/$/, '') ||
  window.location.origin;

export default function InviteModal({ orgId, orgName, open, onClose }: Props) {
  const queryClient = useQueryClient();
  // Ensure we only trigger one auto-generate per open cycle.
  const autoGeneratedRef = useRef<string | null>(null);

  const { data: invites = [], isLoading } = useQuery<InviteRow[]>({
    queryKey: ['org-invites', orgId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${orgId}/invites`);
      return data.data;
    },
    enabled: open && !!orgId,
  });

  const createInvite = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/v1/orgs/${orgId}/invites`, {});
      return data.data as InviteRow;
    },
    onSuccess: (inv) => {
      queryClient.invalidateQueries({ queryKey: ['org-invites', orgId] });
      const url = inviteJoinUrl(joinLinkOrigin, inv.code);
      void navigator.clipboard.writeText(url).then(
        () => toast.success('Invite link created and copied!'),
        () => toast.success('Invite link created'),
      );
    },
    onError: () => toast.error('Failed to create invite link'),
  });

  // When the modal opens and there are no active invites yet, generate one
  // automatically so the user always sees a ready-to-share link without
  // having to click "Generate". Keyed by orgId so reopening for a different
  // practice re-evaluates.
  useEffect(() => {
    if (!open) {
      autoGeneratedRef.current = null;
      return;
    }
    if (isLoading) return;
    if (invites.length > 0) return;
    if (createInvite.isPending) return;
    if (autoGeneratedRef.current === orgId) return;
    autoGeneratedRef.current = orgId;
    createInvite.mutate();
  }, [open, isLoading, invites.length, orgId, createInvite]);

  const revokeInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      await api.delete(`/api/v1/orgs/${orgId}/invites/${inviteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invites', orgId] });
      toast.success('Invite revoked');
    },
    onError: () => toast.error('Failed to revoke invite'),
  });

  function copyLink(code: string) {
    const url = inviteJoinUrl(joinLinkOrigin, code);
    void navigator.clipboard.writeText(url).then(
      () => toast.success('Copied!'),
      () => toast.error('Could not copy'),
    );
  }

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title"
        className="w-full max-w-md rounded-2xl border border-[#1f2937] bg-[#111827] shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
          <h3 id="invite-title" className="text-base font-bold text-white">
            Invite people to {orgName}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="mb-4 text-sm text-slate-400">
            Share an invite link so people can create their account and join your practice.
          </p>

          <button
            type="button"
            onClick={() => createInvite.mutate()}
            disabled={createInvite.isPending}
            className="mb-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            {createInvite.isPending ? 'Generating…' : 'Generate invite link'}
          </button>

          {isLoading ? (
            <p className="text-center text-sm text-slate-400">Loading…</p>
          ) : invites.length > 0 ? (
            <div>
              <h4 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Active links
              </h4>
              <ul className="space-y-2">
                {invites.map((inv) => {
                  const url = inviteJoinUrl(joinLinkOrigin, inv.code);
                  return (
                    <li
                      key={inv.id}
                      className="flex items-center gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs text-slate-400">{url}</p>
                        <p className="mt-0.5 text-xs text-slate-600">
                          {inv.maxUses != null
                            ? `${inv.useCount}/${inv.maxUses} uses`
                            : `${inv.useCount} uses`}
                          {inv.expiresAt && ` · expires ${formatDateAU(inv.expiresAt)}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyLink(inv.code)}
                        className="shrink-0 rounded-lg bg-primary px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-hover"
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => revokeInvite.mutate(inv.id)}
                        disabled={revokeInvite.isPending}
                        className="shrink-0 rounded-lg border border-red-800/50 px-2 py-1 text-xs text-red-400 hover:bg-red-950/50 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
